'use strict';

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { render } from 'react-dom';
import { createRoot } from 'react-dom/client';
import ReactDOM from 'react-dom';
import MediaQuery, { useMediaQuery } from 'react-responsive';
import { createBrowserHistory, createPath } from 'history';
import UniversalRouter from 'universal-router';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { createStore, applyMiddleware } from 'redux';
import thunkMiddleware from 'redux-thunk';
import { Provider } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { library } from '@fortawesome/fontawesome-svg-core';

import { historyManager } from './history';
import routes from './routes';

import ModalImport from '@/views/common/modals/ModalImport';
import ModalAddToRoute from '@/views/common/modals/ModalAddToRoute';
import ModalDeleteItem from '@/views/common/modals/ModalDeleteItem';
import ModalEditAttempt from '@/views/common/modals/ModalEditAttempt';
import ModalDeleteAttempt from '@/views/common/modals/ModalDeleteAttempt';
import ModalCreateUser from '@/views/common/modals/ModalCreateUser';
import ModalEditUser from '@/views/common/modals/ModalEditUser';
import ModalDeleteUser from '@/views/common/modals/ModalDeleteUser';
import ModalUndoComplete from '@/views/common/modals/ModalUndoComplete';
import ModalUserSettings from '@/views/common/modals/ModalUserSettings';
import ModalCreatePlace from '@/views/common/modals/ModalCreatePlace';
import ModalAssignUser from '@/views/common/modals/ModalAssignUser';
import Errors from './views/common/Errors';
import Loading from './views/common/Loading';
import rootReducer from './state/reducers';

import { apiService, errorService } from './state/middlewares';
import { appSelectors, appOperations } from '@/state/domains/app';
import { usersOperations, usersSelectors } from '@/state/domains/users';

//import '@fortawesome/fontawesome-free/scss/fontawesome.scss';

import {
	faAngleDoubleRight,
	faAngleLeft,
	faAngleRight,
	faArrowLeft,
	faArrowRight,
	faArrowUpAZ,
	faBan,
	faBars,
	faCalendarDays,
	faCaretDown,
	faCaretLeft,
	faCaretRight,
	faChartLine,
	faChartPie,
	faCheck,
	faChevronLeft,
	faChevronRight,
	faCircleNotch,
	faClipboardCheck,
	faClipboardList,
	faDoorClosed,
	faExclamation,
	faExchangeAlt,
	faExpand,
	faEye,
	faFileExport,
	faFilter,
	faFlag,
	faForward,
	faGear,
	faGripVertical,
	faHandHolding,
	faHome,
	faListCheck,
	faListOl,
	faLocationDot,
	faLongArrowAltUp,
	faLongArrowAltDown,
	faMagnifyingGlass,
	faMap,
	faMapLocationDot,
	faMapMarkerAlt,
	faMars,
	faPenToSquare,
	faPlus,
	faRoute,
	faScaleBalanced,
	faSignOutAlt,
	faSlidersH,
	faSort,
	faSortUp,
	faSortDown,
	faTachometerAlt,
	faThList,
	faTimes,
	faTrashAlt,
	faTrashCan,
	faTruck,
	faTruckFast,
	faUndoAlt,
	faUpload,
	faUser,
	faUsers,
	faUserPlus,
	faVenus,
	faXmark,
} from '@fortawesome/free-solid-svg-icons';

import {
	faCheckCircle,
	faListAlt,
} from '@fortawesome/free-regular-svg-icons';

library.add(
	faAngleDoubleRight,
	faAngleLeft,
	faAngleRight,
	faArrowLeft,
	faArrowRight,
	faArrowUpAZ,
	faBan,
	faBars,
	faCalendarDays,
	faCaretDown,
	faCaretLeft,
	faCaretRight,
	faChartLine,
	faChartPie,
	faCheck,
	faCheckCircle,
	faChevronLeft,
	faChevronRight,
	faCircleNotch,
	faClipboardCheck,
	faClipboardList,
	faDoorClosed,
	faExclamation,
	faExchangeAlt,
	faExpand,
	faEye,
	faFileExport,
	faFilter,
	faFlag,
	faForward,
	faGear,
	faGripVertical,
	faHandHolding,
	faHome,
	faListAlt,
	faListCheck,
	faListOl,
	faLocationDot,
	faLongArrowAltUp,
	faLongArrowAltDown,
	faMagnifyingGlass,
	faMap,
	faMapLocationDot,
	faMapMarkerAlt,
	faMars,
	faPenToSquare,
	faPlus,
	faRoute,
	faScaleBalanced,
	faSignOutAlt,
	faSlidersH,
	faSort,
	faSortUp,
	faSortDown,
	faTachometerAlt,
	faThList,
	faTimes,
	faTrashAlt,
	faTrashCan,
	faTruck,
	faTruckFast,
	faUndoAlt,
	faUpload,
	faUser,
	faUsers,
	faUserPlus,
	faVenus,
	faXmark,
);

const middleware = applyMiddleware(
	apiService,
	errorService,
	thunkMiddleware
);

const store = createStore(rootReducer, middleware);

//const unsubscribeHistory = configureHistory(history, store);

store.subscribe(() => {
	window.zxcv = store.getState();
});

const ResponsiveWrapper = connect(
	(state, props) => ({
		responsive: appSelectors.getResponsive(state),
	}),
	{
	}
)(
	({ responsive, children, ...props }) => {

		if ('xs' in responsive) {
			return (
				<>
					{ children }
				</>
			);
		}

		return (
			<div />
		);
	}
);

const AppWrapper = connect(
	(state, props) => ({
	}),
	{
		setResponsive: appOperations.setResponsive,
		setLoggedInUser: usersOperations.setLoggedInUser,
	}
)(
	({ setLoggedInUser, setResponsive, children }) => {

		const respObj = {
			xs: useMediaQuery({ minWidth: 576 }),
			sm: useMediaQuery({ minWidth: 768 }),
			md: useMediaQuery({ minWidth: 992 }),
			lg: useMediaQuery({ minWidth: 1200 }),
			xl: useMediaQuery({ minWidth: 1400 }),
			portrait: useMediaQuery({ orientation: 'portrait' }),
			retina: useMediaQuery({ minResolution: '2dppx' }),
		};

		useEffect(
			() => {
				setResponsive(respObj);
				setLoggedInUser();
			},
		);

		return (
			<ResponsiveWrapper>
				{ children }
			</ResponsiveWrapper>
		);
	}
);

const DnDWrapper = connect(
	(state, props) => ({
		responsive: appSelectors.getResponsive(state, props),
	}),
	{
	}
)(
	({ responsive, children }) => {

		const backend = responsive.md ? HTML5Backend : TouchBackend;

		return (
			<DndProvider backend={ backend }>
				{ children }
			</DndProvider>
		);
	}
);

const Modals = connect(
	(state, props) => ({
		modals: appSelectors.getModalStates(state, props),
	}),
	{
	}
)(
	({ modals }) => {
		return (
			<>
				{ modals.importJobs && <ModalImport /> }
				{ modals.addToRoute && <ModalAddToRoute /> }
				{ modals.deleteJobs && <ModalDeleteItem /> }
				{ modals.createUser && <ModalCreateUser /> }
				{ modals.editUser && <ModalEditUser /> }
				{ modals.deleteUser && <ModalDeleteUser /> }
				{ modals.userSettings && <ModalUserSettings /> }
				{ modals.editAttempt && <ModalEditAttempt /> }
				{ modals.deleteAttempt && <ModalDeleteAttempt /> }
				{ modals.undoComplete && <ModalUndoComplete /> }
				{ modals.createPlace && <ModalCreatePlace /> }
				{ modals.assignUser && <ModalAssignUser /> }
			</>
		);
	}
);

const App = ({ children }) => {

	return (
	<Provider store={ store }>
		<AppWrapper>
			<Modals />
			<DnDWrapper>
				<Errors />
				<Loading />
				{ children }
			</DnDWrapper>
		</AppWrapper>
	</Provider>
);
};

const history = createBrowserHistory();

const router = new UniversalRouter(
	routes,
	{
		context: { store },
	}
);

const makeRenderLocation = (router, renderer) => async ({ location: nextLocation }) => {

	store.dispatch({ type: 'app/SET_ROUTER_IN_PROGRESS' });
	const component = await router.resolve(nextLocation);
	store.dispatch({ type: 'app/UNSET_ROUTER_IN_PROGRESS' });

	if (component.redirect) {
		return history.replace(component.redirect);
	}

	renderer(component);
};

const makeRenderer = (root, App) => component => {

	root.render(<App>{ component }</App>);
};

const makeStoreHistory = store => ({ location: nextLocation }) => {

	const { app: { location: locationState }} = store.getState();

	const path = createPath(locationState);
	const nextPath = createPath(nextLocation);

	if (
		path !== nextPath ||
		nextLocation.state ||
		(locationState.state && !nextLocation.state)
	) {
		store.dispatch({
			type: 'app/UPDATE_LOCATION',
			data: nextLocation,
		});
	}
};

const historyMan = historyManager(history);

historyMan.addListener(makeStoreHistory(store));

const run = () => {

	const container = document.getElementById('root');
	const root = createRoot(container);

	historyMan.addListener(makeRenderLocation(router, makeRenderer(root, App)));
	historyMan.triggerLocation({ location: history.location });
	historyMan.listen();
};

if (['complete', 'loaded', 'interactive'].includes(document.readyState) && document.body) {
	run();
} else {
	window.addEventListener('DOMContentLoaded', run, false);
}
